(ns io.daskul.webapp.widgets.record.header
  "The record header (F-060 zone 1, F-061): identity, state chip, and the
   legal transitions.

   NOTHING PASSIVE LIVES HERE. created_at / updated_at used to sit under the
   record title; that is the most valuable spot on the page and audit metadata
   is almost never acted on, so it stays where it was — the sidebar/Details
   foot — rather than being promoted. The header carries only things that get
   acted on: who this is, what state it is in, what you can do next.

   Transitions moved out of the right rail because a transition needing nine
   parameters was punishing to use in a 320px column, and because nothing in
   that rail was emphasised — every card had equal weight.

   Placement, emphasis and the two-button cap are decided by
   `transition-logic/split-for-header`, which is pure and node tested. This
   namespace only renders what it is handed."
  (:require
   [uix.core :as uix :refer [defui $]]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.widgets.display :as display]
   [io.daskul.webapp.widgets.fsm.transitions :as fsm.transitions]
   [io.daskul.webapp.widgets.fsm.transition-logic :as tl]))

(defn- guard-message
  "A guard concerns ONE action, so it renders under that action rather than as
   a banner — nothing is wrong with the record. Authors phrase it as the fix
   (\"Mark a booking attended first\"), not the rule."
  [transition]
  (or (get-in transition [:ui :guard-message])
      (get-in transition [:ui :disabled-reason])))

(defn- blocked? [transition]
  (boolean (or (:disabled? transition) (guard-message transition))))

(defui ^:private bar-button
  "A header bar button. Destructive changes the COLOUR, not the placement —
   they are separate axes, and a destructive action reaching the bar has been
   deliberately placed there by an author.

   Mantine's `disabled` swallows pointer events, so a blocked transition uses
   `data-disabled` + preventDefault instead: the tooltip explaining WHY has to
   be hoverable, and a disappearing or inert action teaches people the UI is
   unreliable. The button keeps its slot either way."
  [{:keys [transition on-launch]}]
  (let [emph (tl/emphasis transition)
        destructive? (tl/destructive? transition)
        reason (guard-message transition)
        stopped? (blocked? transition)
        label (get-in transition [:ui :label])
        props (cond-> {:size "sm"
                       :onClick (fn [e]
                                  (if stopped?
                                    (.preventDefault e)
                                    (on-launch transition)))}
                stopped? (assoc :data-disabled true
                                :style {:pointerEvents "auto" :cursor "not-allowed"
                                        :opacity 0.6})
                (= :primary emph) (assoc :variant "filled")
                (not= :primary emph) (assoc :variant "default")
                destructive? (assoc :color "red"))
        button ($ ui/button props label)]
    (if reason
      ($ ui/tooltip {:label reason :withArrow true :multiline true :w 240} button)
      button)))

(defui record-header
  "Props:
     title        - the record's name
     state        - current FSM state
     state-ui     - its :ui metadata (label, tone)
     transitions  - already through `prepare-transitions`
     on-launch    - (fn [transition]) when one is chosen
     popover-body - element to render inside the confirm popover, when the
                    chosen transition earns one
     back         - optional {:label :on-click}

   The action cluster is the popover's TARGET. A Mantine popover positions
   relative to the element it wraps, so a confirm launched from here has to be
   anchored here — hosting it as a detached sibling puts it in the top-left
   corner of the page. Anchoring the cluster rather than the individual button
   also means a transition launched from the ⋯ menu lands in the same place,
   which is the F-061 rule: identical rendering wherever it is launched, and
   only the anchor differs."
  [{:keys [title state state-ui transitions on-launch back popover-body
           on-dismiss tabs]}]
  (let [{:keys [bar menu]} (tl/split-for-header (vec transitions))
        actions
        ;; Wraps rather than overflows. Once the cluster is full-width on a
        ;; phone, "Schedule New Trial" + "Join an Existing Session" + ⋯ still
        ;; exceed 390px, and nowrap would push the ⋯ off-screen — the overflow
        ;; menu is the one control that must never be unreachable.
        ;;
        ;; F-060 asks for one full-width primary plus overflow on a phone,
        ;; which is a different thing: it needs the SECOND bar button moved
        ;; into the menu, and CSS can hide a button but cannot re-home it.
        ;; That needs max-bar-buttons to become responsive without the
        ;; component reading a width — see #704.
        ($ ui/group {:gap "sm" :wrap "wrap"
                     :style {:width "var(--header-actions-width, auto)"}}
          (for [t bar]
            ($ bar-button {:key (:event t) :transition t :on-launch on-launch}))
          (when (seq menu)
            ($ ui/menu {:position "bottom-end" :withinPortal true}
              ($ ui/menu-target nil
                ($ ui/button {:variant "default" :size "sm"
                              :data-testid "record-header-more"}
                  "⋯"))
              ($ ui/menu-dropdown nil
                (for [t menu]
                  (let [reason (guard-message t)
                        stopped? (blocked? t)]
                    ($ ui/menu-item
                      {:key (:event t)
                       :disabled stopped?
                       :color (when (tl/destructive? t) "red")
                       :onClick #(when-not stopped? (on-launch t))}
                      ($ ui/stack {:gap 0}
                        ($ ui/text {:size "sm"} (get-in t [:ui :label]))
                        (when-let [sub (or reason
                                           (when-let [target (:target t)]
                                             (str state " → " target)))]
                          ($ ui/text {:size "xs" :c "dimmed"} sub))))))))))]
    ($ ui/stack {:gap "xs" :data-testid "record-header"
                 ;; The header is a BAND, not a block sitting on the page.
                 ;; Reference (`.hdr`): the identity zone is --surface-card
                 ;; with a rule under it, and the body below is transparent
                 ;; over --surface-page. That edge is what separates "who this
                 ;; record is" from "what you can edit"; without it the title
                 ;; and the first field are on one continuous surface and the
                 ;; page reads as a single undifferentiated form.
                 ;;
                 ;; It full-bleeds by cancelling the two padded layers it sits
                 ;; inside — Mantine's Container (`padding-inline:
                 ;; var(--mantine-spacing-md)`) and AppShell main
                 ;; (`--app-shell-padding`) — then paying the inset back as its
                 ;; own --page-pad. Both are named rather than summed to 32px,
                 ;; so changing either one keeps the band aligned.
                 ;;
                 ;; Cancelling only one leaves the rule stopping ~16px short of
                 ;; the edge, which reads as a stray divider rather than the
                 ;; boundary of a zone. Main's padding-left already contains the
                 ;; navbar width, so the left edge lands on the navbar, not
                 ;; under it.
                 :style {:background "var(--surface-card)"
                         :borderBottom "1px solid var(--border)"
                         :marginInline "calc(-1 * (var(--mantine-spacing-md) + var(--app-shell-padding)))"
                         ;; Two paddings above it too: AppShell main's, and
                         ;; the page wrapper's `padding-block`. Cancelling only
                         ;; the first left a strip of page surface between the
                         ;; app bar and the band, with the Back link stranded
                         ;; in it and the band's edge cutting through.
                         :marginTop "calc(-1 * (var(--app-shell-padding) + var(--mantine-spacing-md)))"
                         :marginBottom "var(--section-pad)"
                         ;; The inset is the SAME expression as the margin, so
                         ;; the title always lands on the same vertical as the
                         ;; first card below it — including on a phone, where
                         ;; AppShell's padding drops to xs. Using --page-pad
                         ;; here instead put the title 6px left of the content
                         ;; it heads, because this page's shell is padded by
                         ;; Mantine, not by the design system's scale. See #699
                         ;; for unifying the two.
                         ;; FOUR SIDES, no `padding` shorthand. The shorthand
                         ;; resets every side, so it silently discarded an
                         ;; earlier :paddingBottom (measured 20px), and
                         ;; ordering it after left the no-tabs case at 0
                         ;; because a nil value did not fall back to it.
                         ;; Naming each side removes the trap in both
                         ;; directions.
                         ;;
                         ;; With tabs the bottom is 0: the tab row runs to the
                         ;; band's own border, so the active tab's indicator
                         ;; sits ON that rule rather than floating above it
                         ;; with the band's line reading as a stray divider.
                         :paddingTop "var(--section-pad)"
                         :paddingBottom (if tabs "0px" "var(--section-pad)")
                         :paddingLeft "calc(var(--mantine-spacing-md) + var(--app-shell-padding))"
                         :paddingRight "calc(var(--mantine-spacing-md) + var(--app-shell-padding))"}}
      (when back
        ($ ui/anchor {:size "sm" :c "dimmed"
                      :onClick (:on-click back)
                      :style {:display "inline-flex" :alignItems "center"
                              :gap "4px" :cursor "pointer"}}
          ($ icons/icon-arrow-left {:size 14})
          (:label back)))

      ;; WRAPS, deliberately. On a phone --header-actions-width is 100%, so the
      ;; action cluster claims a full row and drops below the title; on desktop
      ;; it is `auto` and everything stays on one line. That is the whole
      ;; mechanism — the component has no breakpoint of its own.
      ;;
      ;; `nowrap` here forced the title to share a line with two buttons at
      ;; 390px, which broke "Pedro Garcia" across two lines and squeezed the
      ;; state chip to "CONT…".
      ($ ui/group {:justify "space-between" :align "flex-start"
                   :style {:rowGap "var(--gutter-gap, 10px)"}}
        ($ ui/group {:gap "sm" :align "center" :wrap "nowrap"
                     :style {:minWidth 0}}
          ;; Carries a testid because it is a styled :div, not a Mantine Title —
          ;; so there is no `heading` role to select it by, and a bare text
          ;; locator collides with the same name elsewhere on the page (the
          ;; Social Media Name field, a sidebar row). A spec that reached for
          ;; getByRole('heading') here matched nothing and only surfaced once an
          ;; unrelated failure ahead of it was fixed.
          ($ :div {:data-testid "record-title"
                   :style {:fontFamily "var(--font-heading)"
                           :fontSize "var(--record-title-size)"
                           :fontWeight "var(--record-title-weight)"
                           :letterSpacing "var(--record-title-tracking)"
                           :color "var(--text)"}}
            title)
          ;; The chip's colour comes from the state's lifecycle :tone (#679),
          ;; never from its :variant — `danger` on a button means red, but on a
          ;; badge it marks the unhappy ENDING, and a red chip on every lost
          ;; record reads as an error someone must fix.
          (when state-ui
            ($ ui/box {:data-testid "record-status" :style {:flexShrink 0}}
              (display/render-display-value
               state (display/state-ui->display-config state state-ui)))))

        ;; Aggregates with no legal transition render title + chip and nothing
        ;; else — no empty affordance. students-v1 is exactly this (#676).
        (when (or (seq bar) (seq menu))
          (if popover-body
            ;; CONTROLLED popover: `opened` is ours, so Mantine cannot close it
            ;; on its own. Outside-click and Escape both fire onChange(false),
            ;; and without this handler the confirm is a trap — the only way
            ;; out is Cancel.
            ($ ui/popover {:opened true
                           :onChange #(when-not % (on-dismiss))
                           :closeOnClickOutside true
                           :closeOnEscape true
                           ;; Escape is `onKeyDownCapture` on the DROPDOWN, so it
                           ;; only fires when focus is inside it. Without a trap,
                           ;; focus stays on the button that opened the confirm and
                           ;; Escape reaches nothing. Trapping is also what a form
                           ;; in a popover owes a keyboard user.
                           :trapFocus true
                           :position "bottom-end"
                           :withArrow true
                           :shadow "lg"
                           :withinPortal true
                           :data-testid "transition-popover"}
              ($ ui/popover-target nil actions)
              ($ ui/popover-dropdown {:style {:maxWidth "340px"}} popover-body))
            actions)))

      ;; The tab strip is the band's last row (F-060). Below it, the tabs read
      ;; as the first thing in the body rather than as part of the record's
      ;; identity — and the band's bottom rule then separated the title from
      ;; its own tabs.
      tabs)))

(defui record-header-with-launcher
  "record-header plus the container the chosen transition opens in. Holds the
   `which transition is open` state so the header and its container cannot
   disagree, and so the popover can be anchored by the header itself."
  [{:keys [title state state-ui transitions on-transition loading-event
           agg-key record back tabs launch-request]}]
  (let [[open set-open!] (uix/use-state nil)
        ;; The drawer's own copy of the chosen transition. It survives `open`
        ;; going nil so the body keeps rendering while the drawer slides out;
        ;; cleared by the drawer's onExited.
        [drawer-tx set-drawer-tx!] (uix/use-state nil)
        fields (when open (fsm.transitions/extract-param-fields open))
        container (when open (tl/container open fields))
        drawer-open? (= :drawer container)
        close! #(set-open! nil)
        commit (fn [event params]
                 (on-transition event params)
                 (set-open! nil))
        _ (uix/use-effect
           (fn []
             (when drawer-open? (set-drawer-tx! open))
             js/undefined)
           [drawer-open? open])

        ;; A banner can launch a transition, and it must land in the SAME
        ;; container the header would have used — F-059 is explicit that a
        ;; transition renders identically wherever it is launched from, or
        ;; people learn to distrust one of the two.
        ;;
        ;; So the request comes in as data (an event id plus a nonce, so
        ;; clicking the same action twice re-fires) and this resolves it
        ;; against the same `transitions` the bar reads. Container resolution,
        ;; guards and the param form are then whatever the header already
        ;; does — there is deliberately no second path.
        _ (uix/use-effect
           (fn []
             (when-let [ev (:event launch-request)]
               (when-let [t (first (filter #(= (str (:event %)) (str ev))
                                           transitions))]
                 (set-open! t)))
             js/undefined)
           [launch-request transitions])
        body (when open
               ($ fsm.transitions/expanded-panel
                 {:transition open
                  :agg-key agg-key
                  :record record
                  :loading? (= loading-event (:event open))
                  :on-cancel close!
                  :on-transition commit
                  ;; The panel needs its host to decide whether `:ui :confirm`
                  ;; can be a nested modal. It cannot be inside a popover —
                  ;; Mantine dismisses one from a document-level mousedown, so
                  ;; the portalled modal reads as an outside click and the
                  ;; press that should have confirmed unmounted it instead
                  ;; (#810). The popover IS the confirmation.
                  :container container}))]
    ($ :<>
      ($ record-header
        {:title title :state state :state-ui state-ui
         :transitions transitions :back back :tabs tabs
         :on-launch set-open!
         :on-dismiss close!

         :popover-body (when (= :popover container) body)})
      ;; Rendered unconditionally so the drawer exists before it opens; see
      ;; transition-drawer. `drawer-tx` outlives `open` by one exit animation.
      ($ fsm.transitions/transition-drawer
        {:opened drawer-open?
         :on-exited #(set-drawer-tx! nil)
         ;; While open, read `open` DIRECTLY rather than the retained copy —
         ;; the retained one is written by an effect, so it is a render behind
         ;; and the drawer would slide in empty for a frame. The retained copy
         ;; is only for the way out, once `open` is already nil.
         :transition (if drawer-open? open drawer-tx)
         :agg-key agg-key
         :record record
         :loading? (= loading-event (:event (if drawer-open? open drawer-tx)))
         :on-cancel close!
         :on-transition commit}))))
